import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Utensils } from "lucide-react";

interface MealItem {
  id: string;
  meal_name: string;
  meal_type: string;
  scheduled_time: string | null;
  day_number: number | null;
  foods: any;
  total_calories: number | null;
  total_protein: number | null;
  total_carbs: number | null;
  total_fat: number | null;
}

interface MealPlan {
  id: string;
  plan_name: string | null;
  start_date: string | null;
  end_date: string | null;
  total_daily_calories: number | null;
  notes: string | null;
  created_at: string;
  patient: {
    full_name: string;
    email: string | null;
  };
}

interface MealPlanPDFContentProps {
  plan: MealPlan;
  mealItems: MealItem[];
}

export function MealPlanPDFContent({ plan, mealItems }: MealPlanPDFContentProps) {
  // Agrupar refeições por dia
  const groupedByDay = mealItems.reduce((acc, item) => {
    const day = item.day_number || 1;
    if (!acc[day]) acc[day] = [];
    acc[day].push(item);
    return acc;
  }, {} as Record<number, MealItem[]>);

  const sortedDays = Object.keys(groupedByDay)
    .map(Number)
    .sort((a, b) => a - b);

  return (
    <div className="bg-white p-8 max-w-4xl mx-auto" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* Cabeçalho */}
      <div className="mb-8 border-b-2 border-green-600 pb-4">
        <div className="flex items-center gap-3 mb-2">
          <Utensils className="h-8 w-8 text-green-600" />
          <h1 className="text-3xl font-bold text-gray-900">Plano Alimentar</h1>
        </div>
        <h2 className="text-xl text-gray-700">{plan.plan_name || "Sem nome"}</h2>
      </div>

      {/* Informações do Paciente */}
      <div className="mb-6 bg-gray-50 p-4 rounded-lg">
        <h3 className="font-semibold text-lg mb-2">Paciente</h3>
        <p className="text-gray-700">
          <strong>Nome:</strong> {plan.patient.full_name}
        </p>
        {plan.patient.email && (
          <p className="text-gray-700">
            <strong>Email:</strong> {plan.patient.email}
          </p>
        )}
      </div>

      {/* Informações do Plano */}
      <div className="mb-6 grid grid-cols-2 gap-4">
        {plan.start_date && (
          <div>
            <p className="text-sm text-gray-600">Início</p>
            <p className="font-semibold">
              {format(new Date(plan.start_date), "dd/MM/yyyy", { locale: ptBR })}
            </p>
          </div>
        )}
        {plan.end_date && (
          <div>
            <p className="text-sm text-gray-600">Término</p>
            <p className="font-semibold">
              {format(new Date(plan.end_date), "dd/MM/yyyy", { locale: ptBR })}
            </p>
          </div>
        )}
        {plan.total_daily_calories && (
          <div>
            <p className="text-sm text-gray-600">Meta Diária</p>
            <p className="font-semibold">{plan.total_daily_calories} kcal</p>
          </div>
        )}
        <div>
          <p className="text-sm text-gray-600">Criado em</p>
          <p className="font-semibold">
            {format(new Date(plan.created_at), "dd/MM/yyyy", { locale: ptBR })}
          </p>
        </div>
      </div>

      {/* Observações */}
      {plan.notes && (
        <div className="mb-6 bg-yellow-50 p-4 rounded-lg">
          <h3 className="font-semibold text-lg mb-2">Observações</h3>
          <p className="text-gray-700 whitespace-pre-wrap">{plan.notes}</p>
        </div>
      )}

      {/* Refeições por Dia */}
      {sortedDays.map((day) => (
        <div key={day} className="mb-8 break-inside-avoid">
          <h3 className="text-xl font-bold text-green-700 mb-4 border-b border-green-200 pb-2">
            Dia {day}
          </h3>
          <div className="space-y-4">
            {groupedByDay[day].map((meal) => (
              <div key={meal.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-semibold text-lg">{meal.meal_name}</h4>
                    {meal.scheduled_time && (
                      <p className="text-sm text-gray-600">
                        Horário: {meal.scheduled_time}
                      </p>
                    )}
                  </div>
                  {meal.total_calories && (
                    <div className="text-right">
                      <p className="font-bold text-green-600">{meal.total_calories} kcal</p>
                      {meal.total_protein !== null && (
                        <p className="text-xs text-gray-600">P: {meal.total_protein}g</p>
                      )}
                      {meal.total_carbs !== null && (
                        <p className="text-xs text-gray-600">C: {meal.total_carbs}g</p>
                      )}
                      {meal.total_fat !== null && (
                        <p className="text-xs text-gray-600">G: {meal.total_fat}g</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Alimentos */}
                {Array.isArray(meal.foods) && meal.foods.length > 0 && (
                  <div className="mt-3">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left p-2">Alimento</th>
                          <th className="text-right p-2">Qtd</th>
                          <th className="text-right p-2">Calorias</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(meal.foods as Array<{ name?: string; food?: string; quantity?: string; amount?: string; calories?: number }>).map((food, idx: number) => (
                          <tr key={idx} className="border-t border-gray-100">
                            <td className="p-2">{food.name || food.food}</td>
                            <td className="text-right p-2">{food.quantity || food.amount}</td>
                            <td className="text-right p-2">
                              {food.calories ? `${food.calories} kcal` : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Rodapé */}
      <div className="mt-8 pt-4 border-t border-gray-300 text-center text-sm text-gray-600">
        <p>Documento gerado em {format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}</p>
        <p className="mt-1">Este plano alimentar foi gerado por IA e deve ser supervisionado por um profissional de saúde.</p>
      </div>
    </div>
  );
}
